<!-- New group: a name and the friends in it. One page to all of them at once.
     Screens role. -->
<template>
  <IonModal
    :is-open="isOpen"
    :initial-breakpoint="1"
    :breakpoints="[0, 1]"
    class="pm-scr-sheet pm-scr-sheet--auto"
    @will-present="onPresent"
    @did-present="focusName"
    @did-dismiss="onDismiss"
  >
    <form class="pm-scr-sheet-body" novalidate @submit.prevent="make.run()">
      <div class="pm-scr-sheet-head">
        <h2 class="section-title">New group</h2>
        <IonButton fill="clear" type="button" @click="$emit('close')">Cancel</IonButton>
      </div>

      <p class="pm-scr-muted pm-scr-small">One page goes to everyone in the group. They'll see it from you, even if they aren't friends with each other.</p>

      <div class="pm-scr-field">
        <label class="pm-scr-label" :for="`${uid}-group-name`">Group name</label>
        <input
          :id="`${uid}-group-name`"
          v-model="name"
          class="pm-scr-input pm-scr-input--lcd pm-scr-upper"
          type="text"
          maxlength="16"
          autocomplete="off"
          autocapitalize="characters"
          placeholder="CREW"
          :aria-invalid="nameError ? 'true' : 'false'"
          :aria-describedby="`${uid}-group-name-err`"
          @input="name = name.toUpperCase().replace(/[^A-Z0-9 .'&!_-]/g, ''); nameError = ''"
        >
        <p :id="`${uid}-group-name-err`" class="field-error" :hidden="!nameError" role="alert">{{ nameError }}</p>
      </div>

      <FriendChecklist v-model="picked" :friends="choices" />

      <button type="submit" class="sr-only" tabindex="-1" aria-hidden="true" />
      <IonButton expand="block" type="button" :disabled="make.busy.value || !choices.length" @click="make.run()">
        {{ make.busy.value ? 'Making…' : picked.length ? `Make group of ${picked.length + 1}` : 'Make group' }}
      </IonButton>
      <p v-if="make.error.value" class="field-error" role="alert">{{ make.error.value }}</p>
    </form>
  </IonModal>
</template>

<script setup>
import { userMessage } from './user-message.js';
import { ref, computed, useId } from 'vue';
import { IonModal, IonButton } from '@ionic/vue';
import { useAction, toast } from '../core/ui/index.js';
import { acceptedFriends, createGroup } from '../store.js';
import FriendChecklist from './FriendChecklist.vue';

const uid = `pm${useId()}`;

defineProps({ isOpen: { type: Boolean, default: false } });
const emit = defineEmits(['close', 'created']);

const name = ref('');
const nameError = ref('');
const picked = ref([]);
const choices = computed(() => acceptedFriends.value.filter((f) => !f.system));

const make = useAction(async () => {
  if (!name.value.trim()) {
    nameError.value = 'Give the group a name — like CREW or FAMILY.';
    document.getElementById(`${uid}-group-name`)?.focus();
    return;
  }
  if (!picked.value.length) throw new Error('Pick at least one friend.');
  const r = await createGroup(name.value, picked.value);
  if (!r?.ok) throw new Error(userMessage(r, "Couldn't make the group. Try again."));
  toast(`${r.group.name} made`);
  emit('created', r.group.id);
  emit('close');
}, { weight: 'pop' });

let opener = null;
function onPresent() {
  opener = document.activeElement;
  name.value = '';
  nameError.value = '';
  picked.value = [];
}
function focusName() {
  try { document.getElementById(`${uid}-group-name`)?.focus(); } catch { /* gone */ }
}
function onDismiss() {
  emit('close');
  const el = opener;
  opener = null;
  try { el?.focus?.(); } catch { /* gone */ }
}
</script>
